import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import {
  resolveEffectiveAiAnalysis,
  type AiAnalysisSettings,
  type District,
} from '@citizen-walk/shared-types';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import {
  effectiveModelName,
  useAiAnalysisSettings,
  useAiCategoryMasters,
  useAiOutputFormats,
} from '../masters';
import { precheckDistrictAiConfig, type PrecheckIssue } from './aiConfigPrecheck';

/**
 * AI 解析設定 panel (DistrictEditForm の「AI 解析設定」fieldset を `/districts/:id/ai` へ移設)。
 *
 * 役割は「この地区で AI 解析を有効化するか」+「どの M1 `aiAnalysisSettings` テンプレを使うか
 * (settingsId)」の選択に限定する (= 統合エディタ地区モードの必須入口)。M1 マスター一覧から
 * `<select>` で選ばせ、地区別にどのテンプレを使っているか一目で分かるようにする。
 *
 * 統合エディタ再設計 Phase 5: 旧「地区別カスタマイズ (override)」編集 UI
 * (outputFormatIdOverride / promptSectionOverrides / fields[] JSON 手書き) は
 * 統合エディタ地区モード (`/districts/:id/ai-editor`、グレー継承+項目ごと上書きトグル) へ
 * 一本化したため本 panel から撤去。settingsId 設定済みなら同エディタへのリンクを表示する。
 *
 * 保存は `District.aiAnalysis` へ `{ enabled, settingsId }` を merge:true で書く
 * (override キーは触れない=エディタが管理する値を温存)。
 *
 * 既存値の取り扱い:
 *   - enabled: 初期 `district.aiAnalysis?.enabled ?? true` (旧 v1 互換、未設定なら有効)
 *   - settingsId: 初期 `district.aiAnalysis?.settingsId ?? ''` (空 = 未選択)
 */

export interface AiAnalysisSettingMaster extends AiAnalysisSettings {
  id: string;
}

/**
 * pure: マスター一覧を `<select>` 用に並べ替える。
 * district.mode と一致する mode のマスターを上位に、その他を下位に置く
 * (各群の内部順序は元配列順 = displayName 昇順を維持)。
 */
export function sortSettingsForMode(
  items: AiAnalysisSettingMaster[],
  mode: District['mode'],
): AiAnalysisSettingMaster[] {
  const matching = items.filter((it) => it.mode === mode);
  const others = items.filter((it) => it.mode !== mode);
  return [...matching, ...others];
}

/**
 * pure: option の表示ラベルを組み立てる
 * (`displayName (id) [mode] モデル=<実際に使われるモデル名>`、無効なら `(無効)` 注記)。
 *
 * モデル名は generationConfig.model 未設定でも空欄にせず、ランタイム既定モデル名 + `(既定)` を出す
 * (どのテンプレが旧モデルのままかを選択時に見分けられるようにするため)。
 */
export function settingOptionLabel(s: AiAnalysisSettingMaster): string {
  const disabledNote = s.enabled === false ? ' (無効)' : '';
  const { model, isDefault } = effectiveModelName(s.generationConfig);
  const modelNote = ` モデル=${model}${isDefault ? ' (既定)' : ''}`;
  return `${s.displayName} (${s.id}) [${s.mode}]${modelNote}${disabledNote}`;
}

export interface DistrictAiAnalysisPanelProps {
  district: District;
  uid: string;
  canEdit: boolean;
}

export function DistrictAiAnalysisPanel({ district, uid, canEdit }: DistrictAiAnalysisPanelProps) {
  const { toast } = useToast();
  const settingsState = useAiAnalysisSettings();
  const mastersState = useAiCategoryMasters();
  const formatsState = useAiOutputFormats();
  const [enabled, setEnabled] = useState<boolean>(district.aiAnalysis?.enabled ?? true);
  const [settingsId, setSettingsId] = useState<string>(district.aiAnalysis?.settingsId ?? '');
  const [saving, setSaving] = useState(false);

  const allSettings: AiAnalysisSettingMaster[] =
    settingsState.status === 'ready' ? settingsState.items : [];
  const sortedSettings = sortSettingsForMode(allSettings, district.mode);
  const selected =
    settingsState.status === 'ready' ? allSettings.find((s) => s.id === settingsId) : undefined;

  // プレチェック: 全データが揃ってから、選択中の設定の実効値を解決して不備を検出する。
  // (テンプレ + 地区上書きを resolveEffectiveAiAnalysis で解決し、出力項目の候補データ欠落・
  //  参照先の語彙データ消失・入力項目の食い違いを一般ユーザ向け文言で列挙する)
  const precheckReady =
    settingsState.status === 'ready' &&
    mastersState.status === 'ready' &&
    formatsState.status === 'ready';
  const precheckIssues = useMemo<PrecheckIssue[]>(() => {
    if (!precheckReady) return [];
    const template = selected;
    const masters = mastersState.status === 'ready' ? mastersState.items : [];
    const formats = formatsState.status === 'ready' ? formatsState.items : [];
    const resolved = template
      ? resolveEffectiveAiAnalysis(template, district.aiAnalysis ?? null)
      : null;
    const outputFormatId = resolved?.outputFormatId.value ?? template?.outputFormatId ?? '';
    const outputFormat = formats.find((f) => f.id === outputFormatId);
    const effectiveFields = resolved?.outputFieldsOverride.value ?? outputFormat?.schema.fields;
    const referencedMasterIds =
      resolved?.categoryMasterIds.value ?? template?.categoryMasterIds ?? [];
    return precheckDistrictAiConfig({
      enabled,
      settingsId,
      template,
      districtMode: district.mode,
      outputFormat,
      effectiveFields,
      availableMasters: masters,
      referencedMasterIds,
      districtInputKeys: district.dataSettings?.postFields?.map((f) => f.key),
      effectiveInputFieldKeys: resolved?.inputFieldKeys.value,
    });
  }, [precheckReady, selected, mastersState, formatsState, district, enabled, settingsId]);

  async function handleSave() {
    if (!canEdit) return;

    // enabled + settingsId のみを merge:true で書く。setDoc の merge:true は **ネスト map を
    // 再帰マージ** する (emulator 実機確認済) ため、`aiAnalysis` 配下の override キー
    // (inputFieldKeys / promptInputMappingOverride / promptSectionOverrides /
    //  generationConfigOverride / outputFieldsOverride / categoryMasterIdsOverride 等) は
    // ここで書かなければ **温存される** = 統合エディタ地区モードが管理する値を壊さない。
    // ⚠ aiAnalysis 全体を渡しても兄弟キーは消えない (旧実装が override 除去に deleteField を
    //    使っていたのもこの再帰マージが理由)。full-replace に「修正」しないこと。
    setSaving(true);
    try {
      const ref = doc(db, 'districts', district.id);
      await setDoc(
        ref,
        {
          aiAnalysis: { enabled, settingsId: settingsId || null },
          updatedAt: serverTimestamp(),
          updatedBy: uid,
        },
        { merge: true },
      );
      toast('success', 'AI 解析設定を保存しました');
    } catch (e) {
      toast('error', `保存失敗: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card padding="lg" className="border-primary-200" data-testid="district-ai-analysis-panel">
      <h3 className="text-base font-bold text-slate-900 mb-3">🤖 AI 解析設定</h3>
      <p className="text-xs text-slate-500 mb-3">この地区で使うAI解析の設定を選びます。</p>

      {settingsId && (
        <p className="text-xs mb-3">
          <Link
            to={`/districts/${district.id}/ai-editor`}
            className="text-primary-600 hover:underline"
            data-testid="ai-analysis-editor-link"
          >
            → 統合エディタ（地区モード）で詳細編集（テンプレ継承グレー＋項目ごと上書き）
          </Link>
        </p>
      )}

      <label className="flex items-center gap-2 mb-3">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          disabled={!canEdit || saving}
          className="h-4 w-4"
          data-testid="ai-analysis-enabled"
        />
        <span className="text-sm">この地区で AI 解析を有効化 (投稿の自動 AI 解析を実行)</span>
      </label>

      {enabled && (
        <div className="ml-6 space-y-3 mb-3">
          <label className="grid grid-cols-1 gap-1">
            <span className="text-xs text-slate-500">使用する AI 解析設定</span>
            <select
              value={settingsId}
              onChange={(e) => setSettingsId(e.target.value)}
              disabled={!canEdit || saving}
              className="min-h-touch px-3 border border-slate-300 rounded text-sm bg-white"
              data-testid="ai-analysis-settings-select"
            >
              <option value="">— 未選択 —</option>
              {settingsState.status === 'loading' && (
                <option value="" disabled>
                  読込中…
                </option>
              )}
              {sortedSettings.map((s) => (
                <option key={s.id} value={s.id}>
                  {settingOptionLabel(s)}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-slate-500 mt-1">
              地区の mode (<code>{district.mode}</code>) と一致するマスターを上位に表示しています。
            </p>
          </label>

          {/* 現在の設定を明示表示 (地区別にどのマスターを使っているか分かるように) */}
          <div className="text-xs" data-testid="ai-analysis-current">
            {settingsId ? (
              selected ? (
                <span className="text-slate-700">
                  現在: <strong>{selected.displayName}</strong> (<code>{selected.id}</code>)
                  {selected.enabled === false && (
                    <span className="text-warning-700"> ⚠ このマスターは無効化されています</span>
                  )}
                </span>
              ) : (
                <span className="text-warning-700">
                  ⚠ 選択中の設定 (<code>{settingsId}</code>) は「AI解析の設定」に登録されていません
                </span>
              )
            ) : (
              <span className="text-danger-700">
                未選択 ⚠ このままだと AI 解析は失敗します （AI解析の設定が選ばれていません）
              </span>
            )}
          </div>

          {/* 設定のプレチェック: 統合エディタを開く前に不備を一般ユーザ向け文言で提示する。
              (設定未選択時は上の赤字メッセージが既に出るため、選択済みのときだけ表示) */}
          {precheckReady && settingsId && (
            <PrecheckBox issues={precheckIssues} districtId={district.id} />
          )}

          {/* 地区別の詳細上書き (出力フォーマット / プロンプト各節 / 出力列 fields[]) は
              統合エディタ地区モードへ一本化 (Phase 5)。settingsId 設定済みなら上部リンクから遷移。 */}
          {settingsId && (
            <p className="text-[11px] text-slate-500">
              出力フォーマット・プロンプト各節・出力列 (fields[]) の地区別上書きは、上の
              <Link
                to={`/districts/${district.id}/ai-editor`}
                className="text-primary-600 hover:underline mx-1"
              >
                統合エディタ（地区モード）
              </Link>
              で「テンプレ継承（グレー）＋項目ごと✓上書き」として編集します。
            </p>
          )}
        </div>
      )}

      <div className="flex justify-end">
        <Button
          onClick={() => void handleSave()}
          disabled={!canEdit || saving}
          data-testid="ai-analysis-save"
        >
          {saving ? '保存中…' : '保存'}
        </Button>
      </div>
    </Card>
  );
}

/**
 * 設定プレチェックの結果表示。問題が無ければ緑、あればエラー (赤) → 警告 (黄) の順に並べる。
 * 文言は precheckDistrictAiConfig が一般ユーザ向けに用意したものをそのまま出す
 * (内部 key は含めない)。修正は「統合エディタ（地区モード）」または「AI解析の設定」で行う旨を案内。
 */
function PrecheckBox({ issues, districtId }: { issues: PrecheckIssue[]; districtId: string }) {
  if (issues.length === 0) {
    return (
      <div
        className="rounded border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-800"
        data-testid="ai-analysis-precheck"
      >
        ✅ 設定のチェック: 問題は見つかりませんでした。
      </div>
    );
  }

  const errors = issues.filter((i) => i.severity === 'error');
  const warnings = issues.filter((i) => i.severity === 'warning');
  const hasError = errors.length > 0;

  return (
    <div
      className={`rounded border p-2 text-xs ${
        hasError ? 'border-danger-200 bg-danger-50' : 'border-amber-200 bg-amber-50'
      }`}
      data-testid="ai-analysis-precheck"
    >
      <p className={`font-bold mb-1 ${hasError ? 'text-danger-800' : 'text-amber-800'}`}>
        {hasError
          ? '⚠ 設定に直すべき点があります（このままだと解析がうまく動かない可能性があります）'
          : '設定に確認したい点があります'}
      </p>
      <ul className="space-y-1.5">
        {[...errors, ...warnings].map((issue, i) => (
          <li key={i} className="flex gap-1.5">
            <span aria-hidden>{issue.severity === 'error' ? '🔴' : '🟡'}</span>
            <span className="text-slate-700">
              <span className="font-medium text-slate-800">{issue.title}</span>
              {issue.hint && <span className="block text-slate-500 mt-0.5">{issue.hint}</span>}
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-slate-500">
        詳しい修正は{' '}
        <Link
          to={`/districts/${districtId}/ai-editor`}
          className="text-primary-600 hover:underline"
        >
          統合エディタ（地区モード）
        </Link>{' '}
        または「AI解析の設定」画面で行えます。
      </p>
    </div>
  );
}
